/**
 * @architecture-header 1
 * @layer module
 * @domain authentication
 * @responsibility Validate and normalize organization-managed student credential input before API submission.
 * @state-owner form consumers
 * @allowed-imports Zod
 * @forbidden API clients; token storage; authorization decisions
 * @public-api organization credential UI internals
 * @side-effects none
 * @tests frontend/packages/workspace-web/src/modules/auth/schemas/organization-credential.test.ts
 * @security-boundary untrusted student credential input
 * @data-classification login codes, one-time codes and passwords remain form-local
 * @trust-model validation improves UX only; FastAPI remains the credential authority
 */

import { z } from 'zod';

const credentialAlphabet = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';
const humanSeparators = /[\s-]+/g;

function normalizeCode(value: string): string {
  return value.replace(humanSeparators, '').toUpperCase();
}

function codeSchema(length: number, label: string) {
  return z
    .string()
    .transform(normalizeCode)
    .refine(
      (value) => new RegExp(`^[${credentialAlphabet}]{${length}}$`).test(value),
      `${label}格式不正確`,
    );
}

export const organizationLoginCodeSchema = codeSchema(6, '機構帳號');
export const organizationOneTimeCodeSchema = codeSchema(8, '一次性代碼');

export const organizationLoginSchema = z.object({
  loginCode: organizationLoginCodeSchema,
  password: z.string().min(1, '請輸入密碼').max(64, '密碼不可超過 64 個字元'),
});

export const organizationActivationSchema = z
  .object({
    loginCode: organizationLoginCodeSchema,
    oneTimeCode: organizationOneTimeCodeSchema,
    newPassword: z.string().min(8, '密碼至少需要 8 個字元').max(64, '密碼不可超過 64 個字元'),
    confirmPassword: z.string(),
    acceptLegal: z.literal(true, '請先同意服務條款與隱私權政策'),
  })
  .refine((value) => value.newPassword === value.confirmPassword, {
    message: '兩次輸入的密碼不一致',
    path: ['confirmPassword'],
  });

export type OrganizationLoginValues = z.infer<typeof organizationLoginSchema>;
export type OrganizationActivationValues = z.infer<typeof organizationActivationSchema>;

export function parseOrganizationCredentialFragment(fragment: string): {
  loginCode: string;
  oneTimeCode: string;
} | null {
  const values = new URLSearchParams(fragment.replace(/^#/, ''));
  const parsed = z
    .object({
      loginCode: organizationLoginCodeSchema,
      oneTimeCode: organizationOneTimeCodeSchema,
    })
    .safeParse({
      loginCode: values.get('account'),
      oneTimeCode: values.get('code'),
    });
  return parsed.success
    ? { loginCode: parsed.data.loginCode, oneTimeCode: parsed.data.oneTimeCode }
    : null;
}
